import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import {
  hackathonsApi,
  orgHackathonsApi,
  studentHackathonsApi,
  type AddComponentRequest,
  type AssignTeamsRequest,
  type CreateHackathonRequest,
  type GradeSubmissionRequest,
  type SubmitProjectFields,
  type UpdateStatusRequest,
} from '../api/hackathons';

// ─── Trainer hooks (full CRUD + grading) ─────────────────────────────────────────

export function useTrainerHackathons(batchId: string) {
  return useQuery({
    queryKey: queryKeys.trainer.batchHackathons(batchId),
    queryFn: async () => {
      const { data } = await hackathonsApi.listByBatch(batchId);
      return data ?? [];
    },
    enabled: !!batchId,
  });
}

export function useHackathonDetail(batchId: string, hackathonId: string) {
  return useQuery({
    queryKey: queryKeys.trainer.hackathonDetail(batchId, hackathonId),
    queryFn: async () => {
      const { data } = await hackathonsApi.get(batchId, hackathonId);
      return data;
    },
    enabled: !!batchId && !!hackathonId,
  });
}

export function useHackathonComponents(batchId: string, hackathonId: string) {
  return useQuery({
    queryKey: queryKeys.trainer.hackathonComponents(batchId, hackathonId),
    queryFn: async () => {
      const { data } = await hackathonsApi.listComponents(batchId, hackathonId);
      return data ?? [];
    },
    enabled: !!batchId && !!hackathonId,
  });
}

export function useHackathonTeams(batchId: string, hackathonId: string) {
  return useQuery({
    queryKey: queryKeys.trainer.hackathonTeams(batchId, hackathonId),
    queryFn: async () => {
      const { data } = await hackathonsApi.listTeams(batchId, hackathonId);
      return data ?? [];
    },
    enabled: !!batchId && !!hackathonId,
  });
}

export function useHackathonLeaderboard(batchId: string, hackathonId: string) {
  return useQuery({
    queryKey: queryKeys.trainer.hackathonLeaderboard(batchId, hackathonId),
    queryFn: async () => {
      const { data } = await hackathonsApi.getLeaderboard(batchId, hackathonId);
      return data ?? [];
    },
    enabled: !!batchId && !!hackathonId,
    refetchInterval: 15_000,
  });
}

export function useCreateHackathon(batchId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateHackathonRequest) => hackathonsApi.create(batchId, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.trainer.batchHackathons(batchId) });
    },
  });
}

export function useAddHackathonComponent(batchId: string, hackathonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: AddComponentRequest) =>
      hackathonsApi.addComponent(batchId, hackathonId, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.trainer.hackathonComponents(batchId, hackathonId) });
      qc.invalidateQueries({ queryKey: queryKeys.trainer.hackathonDetail(batchId, hackathonId) });
    },
  });
}

export function useAssignHackathonTeams(batchId: string, hackathonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: AssignTeamsRequest) =>
      hackathonsApi.assignTeams(batchId, hackathonId, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.trainer.hackathonTeams(batchId, hackathonId) });
      qc.invalidateQueries({ queryKey: queryKeys.trainer.hackathonDetail(batchId, hackathonId) });
    },
  });
}

export function useGradeHackathonSubmission(batchId: string, hackathonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ submissionId, req }: { submissionId: string; req: GradeSubmissionRequest }) =>
      hackathonsApi.gradeSubmission(batchId, hackathonId, submissionId, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.trainer.hackathonLeaderboard(batchId, hackathonId) });
    },
  });
}

export function useUpdateHackathonStatus(batchId: string, hackathonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UpdateStatusRequest) => hackathonsApi.updateStatus(batchId, hackathonId, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.trainer.hackathonDetail(batchId, hackathonId) });
      qc.invalidateQueries({ queryKey: queryKeys.trainer.batchHackathons(batchId) });
    },
  });
}

// ─── Org-admin hooks (read-only) ─────────────────────────────────────────────────

export function useOrgHackathons(batchId: string) {
  return useQuery({
    queryKey: queryKeys.org.batchHackathons(batchId),
    queryFn: async () => {
      const { data } = await orgHackathonsApi.listByBatch(batchId);
      return data ?? [];
    },
    enabled: !!batchId,
  });
}

export function useOrgHackathonLeaderboard(batchId: string, hackathonId: string) {
  return useQuery({
    queryKey: queryKeys.org.hackathonLeaderboard(batchId, hackathonId),
    queryFn: async () => {
      const { data } = await orgHackathonsApi.getLeaderboard(batchId, hackathonId);
      return data ?? [];
    },
    enabled: !!batchId && !!hackathonId,
    refetchInterval: 15_000,
  });
}

// ─── Student / LMS hooks (read-only) ─────────────────────────────────────────────

export function useStudentHackathons(batchId: string) {
  return useQuery({
    queryKey: queryKeys.lms.hackathons(batchId),
    queryFn: async () => {
      const { data } = await studentHackathonsApi.listByBatch(batchId);
      return data ?? [];
    },
    enabled: !!batchId,
  });
}

// There is no backend "all batches" hackathons endpoint (unlike useHome/
// useUpcoming, which have server-side aggregation) — so "All Courses" mode
// fans out one request per enrolled batch and merges the results client-side.
export function useStudentHackathonsAllBatches(batchIds: string[]) {
  const results = useQueries({
    queries: batchIds.map((batchId) => ({
      queryKey: queryKeys.lms.hackathons(batchId),
      queryFn: async () => {
        const { data } = await studentHackathonsApi.listByBatch(batchId);
        return data ?? [];
      },
      enabled: !!batchId,
    })),
  });

  return {
    data: results.flatMap((r) => r.data ?? []),
    isLoading: results.some((r) => r.isLoading),
  };
}

export function useStudentHackathonDetail(batchId: string, hackathonId: string) {
  return useQuery({
    queryKey: queryKeys.lms.hackathonDetail(batchId, hackathonId),
    queryFn: async () => {
      const { data } = await studentHackathonsApi.get(batchId, hackathonId);
      return data;
    },
    enabled: !!batchId && !!hackathonId,
  });
}

export function useStudentHackathonComponents(batchId: string, hackathonId: string) {
  return useQuery({
    queryKey: queryKeys.lms.hackathonComponents(batchId, hackathonId),
    queryFn: async () => {
      const { data } = await studentHackathonsApi.listComponents(batchId, hackathonId);
      return data ?? [];
    },
    enabled: !!batchId && !!hackathonId,
  });
}

export function useStudentHackathonLeaderboard(batchId: string, hackathonId: string) {
  return useQuery({
    queryKey: queryKeys.lms.hackathonLeaderboard(batchId, hackathonId),
    queryFn: async () => {
      const { data } = await studentHackathonsApi.getLeaderboard(batchId, hackathonId);
      return data ?? [];
    },
    enabled: !!batchId && !!hackathonId,
    refetchInterval: 15_000,
  });
}

export function useMyHackathonTeam(batchId: string, hackathonId: string) {
  return useQuery({
    queryKey: queryKeys.lms.hackathonMyTeam(batchId, hackathonId),
    queryFn: async () => {
      const { data } = await studentHackathonsApi.getMyTeam(batchId, hackathonId);
      return data;
    },
    enabled: !!batchId && !!hackathonId,
    retry: false, // 404 (no team assigned) is an expected, not-worth-retrying state
  });
}

export function useHackathonProjectSubmissions(
  batchId: string,
  hackathonId: string,
  componentId: string
) {
  return useQuery({
    queryKey: queryKeys.lms.hackathonProjectSubmissions(batchId, hackathonId, componentId),
    queryFn: async () => {
      const { data } = await studentHackathonsApi.listProjectSubmissions(
        batchId,
        hackathonId,
        componentId
      );
      return data ?? [];
    },
    enabled: !!batchId && !!hackathonId && !!componentId,
  });
}

export function useSubmitHackathonProject(
  batchId: string,
  hackathonId: string,
  componentId: string
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fields: SubmitProjectFields) =>
      studentHackathonsApi.submitProject(batchId, hackathonId, componentId, fields),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.lms.hackathonProjectSubmissions(batchId, hackathonId, componentId),
      });
    },
  });
}
